import styled, { css } from "styled-components";
import { FC, useState } from "react";
import { AnimatePresence } from "framer-motion";

import Button from "@/components/Button";
import ScenesDropdown from "./ScenesDropdown";
import { ProductionSceneResource } from "@/types/resources";
import SceneCreationModal from "./SceneCreationModal";
import Text from "@/components/Text";
import ImageFormModal from "./ImageFormModal";
import ImagesModal from "./ImagesModal";

const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const BoxInner = styled.div`
  display: flex;
  justify-content: space-between;
  height: 40px;
`;

const ControlsButton = styled(Button)`
  border-radius: 0;
  border: 0;
  border-right: 1px solid ${({ theme }) => theme.colors.lightGray};
  height: 100%;
  padding: 0;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover,
  &:active {
    border-color: ${({ theme }) => theme.colors.lightGray};
  }
  &:disabled {
    cursor: not-allowed;
    &:hover {
      color: ${({ theme }) => theme.colors.gray};
    }
  }
`;

const PrintButton = styled(ControlsButton)<{ $fillWithBackground?: boolean }>`
  width: 55px;
  border: 0;
  border-left: 1px solid ${({ theme }) => theme.colors.lightGray};

  ${({ $fillWithBackground }) =>
    $fillWithBackground &&
    css`
      background: ${({ theme }) => theme.colors.lightGray};
      color: ${({ theme }) => theme.colors.black};
      svg path {
        fill: ${({ theme }) => theme.colors.lightGray} !important;
      }
    `}
`;

const Buttons = styled.div`
  display: flex;
`;

const Images = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  display: flex;
  align-items: center;
`;

const ImagesInner = styled.div`
  display: flex;
  flex: 1;
  padding: 8px 0;
  justify-content: space-between;
  align-items: center;
  padding-right: 12px;
  padding-left: 16px;
  border-right: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const AddImageWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
`;

const AddImageButton = styled(Button)`
  width: 31px;
  height: 31px;
  padding: 0;
  svg {
    margin: 0;
  }
`;

const ShowImagesButton = styled(Button)`
  padding-left: 24px;
  padding-right: 24px;
`;

const ImagesCountBadge = styled(Text)`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  background: ${({ theme }) => theme.colors.purpleLighter};
`;

interface Props {
  scenes: ProductionSceneResource[];
  scriptId: number;
  activeScene: ProductionSceneResource;
  setActiveSceneId: (
    sceneId: number | null,
    ingoreDirtyCheck?: boolean
  ) => void;
  print: () => void;
  setCommentsEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  commentsEnabled: boolean;
}

const ScenesBlock: FC<Props> = ({
  scenes,
  scriptId,
  activeScene,
  setActiveSceneId,
  setCommentsEnabled,
  commentsEnabled,
  print,
}) => {
  const [showSceneCreationModal, setShowSceneCreationModal] = useState(false);
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [showImageForm, setShowImageForm] = useState(false);

  const activeSceneIndex = scenes.findIndex((s) => s.id === activeScene.id);

  const onPrevScene = () => {
    const prevSceneId = scenes.find(
      (_, index) => index === activeSceneIndex - 1
    )?.id;

    if (!prevSceneId) return;

    setActiveSceneId(prevSceneId);
  };

  const onNextScene = () => {
    const nextScene = scenes.find(
      (_, index) => index === activeSceneIndex + 1
    )?.id;

    if (!nextScene) return;

    setActiveSceneId(nextScene);
  };

  const toggleCommentMode = () => {
    setCommentsEnabled((prev) => !prev);
  };

  return (
    <>
      <Box>
        <Images>
          <ImagesInner>
            <Text $type="smallHeadline">Images & Descriptions</Text>
            <ShowImagesButton
              color="secondary"
              small
              type="button"
              onClick={() => setShowImagesModal(true)}
              disabled={!activeScene.images?.length}
            >
              <ImagesCountBadge $color="white" $type="extraSmall" as="span">
                {activeScene.images?.length ?? 0}
              </ImagesCountBadge>
              Show Images
            </ShowImagesButton>
          </ImagesInner>
          <AddImageWrap>
            <AddImageButton
              color="secondary"
              small
              type="button"
              onClick={() => setShowImageForm(true)}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 13 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.07129 6.5H9.92843"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.5 3.07227L6.5 9.92941"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </AddImageButton>
          </AddImageWrap>
        </Images>
        <BoxInner>
          <Buttons>
            <ScenesDropdown
              scenes={scenes}
              activeScene={activeScene}
              setActiveSceneId={setActiveSceneId}
            />
            <ControlsButton
              color="action"
              onClick={onPrevScene}
              disabled={activeSceneIndex === 0}
            >
              <svg
                width="7"
                height="10"
                viewBox="0 0 7 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.7075 0.293289C6.3172 -0.0974538 5.68403 -0.097808 5.29329 0.292498L1.28881 4.2925C1.10103 4.48007 0.995522 4.73459 0.995522 5C0.995522 5.26541 1.10103 5.51993 1.28881 5.7075L5.29329 9.7075C5.68403 10.0978 6.3172 10.0975 6.7075 9.70671C7.09781 9.31597 7.09745 8.6828 6.70671 8.2925L3.41053 5L6.70671 1.7075C7.09745 1.3172 7.09781 0.684032 6.7075 0.293289Z"
                  fill="currentColor"
                />
              </svg>
            </ControlsButton>
            <ControlsButton
              color="action"
              onClick={onNextScene}
              disabled={activeSceneIndex === scenes.length - 1}
            >
              <svg
                width="7"
                height="10"
                viewBox="0 0 7 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.292498 0.293289C0.682804 -0.0974538 1.31597 -0.097808 1.70671 0.292498L5.71119 4.2925C5.89897 4.48007 6.00448 4.73459 6.00448 5C6.00448 5.26541 5.89897 5.51993 5.71119 5.7075L1.70671 9.7075C1.31597 10.0978 0.682803 10.0975 0.292498 9.70671C-0.097808 9.31597 -0.0974538 8.6828 0.293289 8.2925L3.58947 5L0.293289 1.7075C-0.0974538 1.3172 -0.097808 0.684032 0.292498 0.293289Z"
                  fill="currentColor"
                />
              </svg>
            </ControlsButton>
            <ControlsButton
              color="action"
              onClick={() => setShowSceneCreationModal(true)}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 7H11"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7 3L7 11"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ControlsButton>
          </Buttons>
          <Buttons>
            <PrintButton
              color="action"
              onClick={toggleCommentMode}
              type="button"
              $fillWithBackground={commentsEnabled}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.00002 15.25H0.75V7.99998C0.75 7.0479 0.937526 6.10514 1.30187 5.22553C1.66622 4.34593 2.20025 3.54669 2.87347 2.87347C3.54669 2.20025 4.34593 1.66622 5.22554 1.30187C6.10514 0.937526 7.0479 0.75 7.99998 0.75H8C8.95208 0.75 9.89485 0.937527 10.7745 1.30187C11.6541 1.66622 12.4533 2.20025 13.1265 2.87348C13.7997 3.5467 14.3338 4.34594 14.6981 5.22555C15.0625 6.10516 15.25 7.04792 15.25 8V8.00002C15.25 9.92283 14.4862 11.7669 13.1265 13.1265C11.7669 14.4862 9.92283 15.25 8.00002 15.25Z"
                  fill="white"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </PrintButton>
            <PrintButton color="action" onClick={print} type="button">
              <svg
                width="17"
                height="16"
                viewBox="0 0 17 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.64258 4.14851V1.18555H13.3569V4.14851"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.3569 9.48145H3.64258V14.5185H13.3569V9.48145Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.6423 11.2595H0.910156V5.33362C0.910156 4.67906 1.49912 4.14844 2.22563 4.14844H14.7733C15.4998 4.14844 16.0887 4.67906 16.0887 5.33362V11.2595H13.3566"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </PrintButton>
          </Buttons>
        </BoxInner>
      </Box>
      <AnimatePresence>
        {showSceneCreationModal && (
          <SceneCreationModal
            scriptId={scriptId}
            setActiveSceneId={setActiveSceneId}
            onClose={() => setShowSceneCreationModal(false)}
          />
        )}
        {showImageForm && (
          <ImageFormModal
            onClose={() => setShowImageForm(false)}
            sceneId={activeScene.id}
          />
        )}
        {showImagesModal && (
          <ImagesModal
            onClose={() => setShowImagesModal(false)}
            images={activeScene.images ?? []}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ScenesBlock;
